import React, { useEffect, useState } from "react";
import { FiX, FiPhone, FiMail, FiCalendar, FiUser, FiAlertCircle, FiLoader, FiBriefcase, FiFileText } from "react-icons/fi";
import { SERVER_URL } from "../../../databucket";

const ClientContextPanel = ({ open, onClose, entityType, entityId }) => {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    if (!open || !entityType || !entityId) return;
    fetchContext();
    fetchAuditTrail();
  }, [open, entityType, entityId]);

  const fetchContext = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/bot/entity-context?entityType=${entityType}&entityId=${entityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setContext(data.context || null);
    } catch (err) {
      console.error("Context fetch error:", err);
    }
    setLoading(false);
  };

  const fetchAuditTrail = async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/bot/audit-trail?entityType=${entityType}&entityId=${entityId}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAuditLogs(data.logs || []);
    } catch {
      setAuditLogs([]);
    }
  };

  if (!open) return null;

  return (
    <div className="context-panel">
      <div className="context-panel__header">
        <div className="context-panel__header-title">
          {entityType === "lead" ? <FiBriefcase size={14} /> : <FiFileText size={14} />}
          <span>{entityType === "lead" ? "Lead" : entityType === "request" ? "Request" : "Client"} Context</span>
        </div>
        <button onClick={onClose} type="button" className="context-panel__close">
          <FiX size={16} />
        </button>
      </div>

      {loading ? (
        <div className="context-panel__loading">
          <FiLoader className="ai-panel__spinner" size={20} />
        </div>
      ) : context ? (
        <>
          {/* Entity Summary */}
          <div className="context-panel__section">
            <div className="context-panel__entity-name">{context.entityName}</div>
            <div className="context-panel__meta-grid">
              <div className="context-panel__meta-item">
                <span className="context-panel__meta-label">Status</span>
                <span className={`context-panel__status-badge context-panel__status-badge--${(context.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                  {context.status || "N/A"}
                </span>
              </div>
              {context.stage && (
                <div className="context-panel__meta-item">
                  <span className="context-panel__meta-label">Stage</span>
                  <span className="context-panel__meta-value">{context.stage}</span>
                </div>
              )}
              {context.priority && (
                <div className="context-panel__meta-item">
                  <span className="context-panel__meta-label">Priority</span>
                  <span className="context-panel__meta-value">{context.priority}</span>
                </div>
              )}
              {context.assignedToName && (
                <div className="context-panel__meta-item">
                  <span className="context-panel__meta-label">Assigned To</span>
                  <span className="context-panel__meta-value">
                    <FiUser size={11} /> {context.assignedToName}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Activity Stats */}
          <div className="context-panel__section">
            <div className="context-panel__title">Activity</div>
            <div className="context-panel__stats">
              {context.totalCallsCount > 0 && (
                <div className="context-panel__stat">
                  <FiPhone size={13} />
                  <span>{context.totalCallsCount} calls</span>
                </div>
              )}
              {context.totalEmailsCount > 0 && (
                <div className="context-panel__stat">
                  <FiMail size={13} />
                  <span>{context.totalEmailsCount} emails</span>
                </div>
              )}
              {context.openRequestsCount > 0 && (
                <div className="context-panel__stat">
                  <FiAlertCircle size={13} />
                  <span>{context.openRequestsCount} open requests</span>
                </div>
              )}
              {context.lastContactDate && (
                <div className="context-panel__stat">
                  <FiCalendar size={13} />
                  <span>Last contact: {new Date(context.lastContactDate).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          {context.recentActivity?.length > 0 && (
            <div className="context-panel__section">
              <div className="context-panel__title">Recent Activity</div>
              {context.recentActivity.map((a, i) => (
                <div key={i} className="context-panel__activity-item">{a}</div>
              ))}
            </div>
          )}

          {/* Audit Trail */}
          {auditLogs.length > 0 && (
            <div className="context-panel__section">
              <div className="context-panel__title">Chat Audit Trail</div>
              {auditLogs.map((log) => (
                <div key={log.id} className="context-panel__audit-item">
                  <div className="context-panel__audit-actor">{log.actorName || "System"}</div>
                  <div className="context-panel__audit-summary">{log.summary}</div>
                  <div className="context-panel__audit-time">
                    {new Date(log.createdAt).toLocaleDateString()} {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="context-panel__empty">No context available</div>
      )}
    </div>
  );
};

export default ClientContextPanel;
